import { TransformedLayer, addResource, type Resource } from './resourceUtils';
import { Product } from '../data/mockData';
import { generateRandomCode } from './common';

/**
 * 转换函数：将原始design数据转换为编辑用的格式
 */
export const transformDesignForEditing = (design: any, canvasWidth: number, canvasHeight: number) => {
  if (!design || !design.faces || !design.faces[0] || !design.faces[0].layers) return { layers: [], printArea: null };
  design.faces[0].layers = design.faces[0].layers.map((layer: any, index: number) => ({
    id: layer.type + "-" + index,
    ...layer,
  }));
  let faceinfo = JSON.parse(JSON.stringify(design.faces[0]));
  return faceinfo;
};

/**
 * 转换函数：将编辑后的数据转换回原始格式
 */
export const transformDesignBackToOriginal = (transformedLayers: TransformedLayer[], originalDesign: any, canvasWidth: number, canvasHeight: number, background: any) => {
  transformedLayers.forEach(layer => {
    if (layer.type === 'Image') {
      layer.src = layer.src?.split("?")[0] || "";
    } else if (layer.type === 'Circle') {
      console.log(layer.fill);
    }
    if (layer.effectType) {
      console.log("layer.effectData", layer.effectData);
    }
    // 已经做了定位的
    if (layer.left && layer.top) {
      layer.options = {
        left: layer.left / canvasWidth,
        top: layer.top / canvasHeight,
        width: layer.width * layer.scaleX / canvasWidth,
        height: layer.height * layer.scaleY / canvasHeight,
      }
    }
  })
  originalDesign.faces[0].layers = transformedLayers;
  return originalDesign;
};

/**
 * 清理过期缓存的函数
 */
export const cleanupExpiredCache = () => {
  try {
    const expiredKeys: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('design_')) {
        const cachedData = localStorage.getItem(key);
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);

          if (parsedData.expiresAt) {
            const expiresAt = new Date(parsedData.expiresAt);
            const now = new Date();

            if (now > expiresAt) {
              expiredKeys.push(key);
            }
          }
        }
      }
    }

    // 清理过期的缓存
    expiredKeys.forEach(key => {
      localStorage.removeItem(key);
      console.log('Cleaned up expired cache:', key);
    });

    if (expiredKeys.length > 0) {
      console.log(`Cleaned up ${expiredKeys.length} expired cache entries`);
    }
  } catch (error) {
    console.error('Error cleaning up expired cache:', error);
  }
};

/**
 * 保存到本地缓存的函数
 */
export const saveToLocalStorage = (productData: Product | undefined, cacheKey: string | null = null) => {
  if (!productData) {
    alert('No design to save');
    return null;
  }

  try {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 1天后过期

    const cacheData = {
      key: cacheKey,
      product: productData,
      timestamp: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      version: '1.0'
    };
    localStorage.setItem(`design_${cacheKey}`, JSON.stringify(cacheData));

    // 显示成功消息
    // alert(`Design saved successfully! Cache key: ${cacheKey}\nExpires: ${expiresAt.toLocaleString()}`);

    return cacheKey;
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    alert('Failed to save design to cache');
    return null;
  }
};

/**
 * 从本地缓存读取设计的函数
 */
export const loadFromLocalStorage = (cacheKey: string) => {
  try {
    const cachedData = localStorage.getItem(`design_${cacheKey}`);
    if (cachedData) {
      const parsedData = JSON.parse(cachedData);

      // 检查是否过期
      if (parsedData.expiresAt) {
        const expiresAt = new Date(parsedData.expiresAt);
        const now = new Date();

        if (now > expiresAt) {
          // 缓存已过期，删除它
          localStorage.removeItem(`design_${cacheKey}`);
          alert('Design has expired and has been removed from cache');
          return null;
        }
      }

      if (parsedData.product) {
        console.log('Design loaded from cache:', cacheKey);

        // 显示剩余时间
        if (parsedData.expiresAt) {
          const expiresAt = new Date(parsedData.expiresAt);
          const now = new Date();
          const remainingTime = expiresAt.getTime() - now.getTime();
          const remainingHours = Math.floor(remainingTime / (1000 * 60 * 60));
          const remainingMinutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));

          console.log(`Cache expires in ${remainingHours}h ${remainingMinutes}m`);
        }

        return parsedData.product;
      }
    }
    alert('Design not found in cache');
    return null;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    alert('Failed to load design from cache');
    return null;
  }
};

/**
 * 从历史记录读取设计的函数
 */
export const loadFromHistory = (historyKey: string, shouldRedirect: boolean = false) => {
  try {
    const historyData = localStorage.getItem(`history_${historyKey}`);
    if (historyData) {
      const parsedData = JSON.parse(historyData);

      if (parsedData.product) {
        if (shouldRedirect) {
          // 跳转到 customize 页面，传递历史键作为 cacheKey
          window.location.href = `/customize/tshirt-1?cacheKey=${historyKey}`;
        } else {
          console.log('Design loaded from history:', historyKey);
        }
        return parsedData.product;
      }
    }
    if (shouldRedirect) {
      alert('Design not found in history');
    }
    return null;
  } catch (error) {
    console.error('Error loading from history:', error);
    if (shouldRedirect) {
      alert('Failed to load design from history');
    }
    return null;
  }
};

/**
 * 获取所有历史记录的函数
 */
export const getAllHistoryDesigns = () => {
  try {
    const designs: Array<{ key: string, product: Product, timestamp: string }> = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('history_')) {
        const historyData = localStorage.getItem(key);
        if (historyData) {
          const parsedData = JSON.parse(historyData);

          if (parsedData.product) {
            designs.push({
              key: parsedData.key,
              product: parsedData.product,
              timestamp: parsedData.timestamp
            });
          }
        }
      }
    }

    return designs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  } catch (error) {
    console.error('Error getting history designs:', error);
    return [];
  }
};

/**
 * 获取所有缓存设计的函数
 */
export const getAllCachedDesigns = () => {
  try {
    const designs: Array<{ key: string, product: Product, timestamp: string, expiresAt: string, isExpired: boolean }> = [];
    const expiredKeys: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('design_')) {
        const cachedData = localStorage.getItem(key);
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);

          // 检查是否过期
          let isExpired = false;
          if (parsedData.expiresAt) {
            const expiresAt = new Date(parsedData.expiresAt);
            const now = new Date();
            isExpired = now > expiresAt;

            if (isExpired) {
              expiredKeys.push(key);
            }
          }

          if (parsedData.product) {
            designs.push({
              key: parsedData.key,
              product: parsedData.product,
              timestamp: parsedData.timestamp,
              expiresAt: parsedData.expiresAt || '',
              isExpired: isExpired
            });
          }
        }
      }
    }

    // 清理过期的缓存
    expiredKeys.forEach(key => {
      localStorage.removeItem(key);
      console.log('Removed expired cache:', key);
    });

    return designs
      .filter(design => !design.isExpired) // 过滤掉过期的设计
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  } catch (error) {
    console.error('Error getting cached designs:', error);
    return [];
  }
};

/**
 * 计算文本宽度的辅助函数
 */
export const calculateTextWidth = (text: string, fontFamily: string, fontSize: number): number => {
  // 创建临时canvas来计算文本宽度
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = `${fontSize}px ${fontFamily}`;
    const metrics = context.measureText(text);
    return metrics.width;
  }
  // 如果无法计算，返回估算值
  return text.length * fontSize * 0.6;
};

/**
 * 将资源应用到产品的所有模板图层，并在无图层时按比例填充背景图片
 */
export const applyResourcesToProducts = (products: any[], resources: Resource[]) => {

  const productsWithUpdatedLayers = products.map(product => {
    if (product.templates && product.templates.length > 0) {
      // 处理所有模板，不仅仅是第一个
      const updatedTemplates = product.templates.map((template: any) => {
        if (template.design && template.design.faces && template.design.faces[0] &&
          template.design.faces[0].layers) {
          const face = template.design.faces[0];

          // 转换设计图层为 TransformedLayer 格式
          const transformedLayers: TransformedLayer[] = face.layers.map((layer: any, index: number) => ({
            id: `layer-${index}`,
            type: layer.type,
            text: layer.text,
            src: layer.src,
            ai: layer.ai,
            shapeType: layer.options?.shapeType,
            left: layer.options?.left || 0,
            top: layer.options?.top || 0,
            width: layer.options?.width || 1,
            height: layer.options?.height || 1,
            lock: layer.options?.lock || false,
            fontSize: layer.options?.fontSize || 16,
            fontFamily: layer.options?.fontFamily,
            fontWeight: layer.options?.fontWeight,
            rotation: layer.options?.rotation || 0,
            frameSize: layer.options?.frameSize,
            fill: layer.options?.fill,
            textAlign: layer.options?.textAlign,
            shadow: layer.options?.shadow,
            transform: layer.options?.transform,
            frameStyle: layer.options?.frameStyle,
            zIndex: layer.options?.zIndex || 100 - index,
          }));

          // 应用资源到图层
          const updatedLayers = addResource(transformedLayers, resources);

          // 转换回原始格式并更新模板
          const updatedFace = {
            ...face,
            layers: updatedLayers.map(layer => ({
              type: layer.type as 'public' | 'print_area' | 'image' | 'text' | 'frame' | 'shape',
              text: layer.text,
              src: layer.src,
              ai: layer.ai,
              shapeType: layer.shapeType,
              left: layer.left,
              top: layer.top,
              options: layer.options,
              width: layer.width,
              height: layer.height,
              lock: layer.lock,
              fontSize: layer.fontSize,
              fontFamily: layer.fontFamily,
              fontWeight: layer.fontWeight,
              rotation: layer.angle,
              fill: layer.fill,
              textAlign: layer.textAlign,
              shadow: layer.shadow,
              transform: layer.transform,
            }))
          };

          // 如果原始图层为空且resources中有图片，设置背景
          if (face.layers.length === 0 && resources && resources.length > 0) {
            // 查找第一个图片资源
            const imageResource =
              // 优先选择被标记为 focus 的图片资源
              resources.find(resource =>
                resource.type === 'image' && resource.content && ((resource as any)?.focus === true)
              ) ||
              // 若没有 focus 的图片，则选择第一个可用的图片资源
              resources.find(resource =>
                resource.type === 'image' && resource.content
              );
            
            // 基于资源宽高与 print_area 宽高比计算：短边铺满（保持比例），并居中裁剪
            const iw = (imageResource as any)?.width ?? 1;
            const ih = (imageResource as any)?.height ?? 1;
            const pw = face?.print_area?.width ?? 1;
            const ph = face?.print_area?.height ?? 1;
            const arImage = iw > 0 && ih > 0 ? iw / ih : 1; // 图片宽高比
            const arCanvas = pw > 0 && ph > 0 ? pw / ph : 1; // 画布（print_area）宽高比
            let normWidth = 1;
            let normHeight = 1;
            let normLeft = 0;
            let normTop = 0;
            let scale = 1;

            if (arImage >= arCanvas) {
              // 图片更宽或相等：以图片短边（高）铺满画布高
              // 缩放后宽度 = (图片宽/图片高) / (画布宽/画布高) = arImage / arCanvas 
              normWidth = arImage / arCanvas; // 宽可能 > 1
              scale = normWidth;
              normLeft = -(normWidth - 1) / 2; // 居中裁剪
              normTop = 0;
            } else {
              // 图片更高：以图片短边（宽）铺满画布宽
              // 缩放后高度 = (画布宽/画布高) / (图片宽/图片高) = arCanvas / arImage 
              normHeight = arCanvas / arImage; // 高可能 > 1
              normLeft = 0;
              scale = normHeight;
              normTop = -(normHeight - 1) / 2; // 居中裁剪
            }

            // console.log('imageResource', imageResource);
            if ((imageResource?.id && imageResource.id.includes("upload-")) && imageResource?.content?.includes("png")) {
              // PNG上传图片：以长边铺满画布（确保整个图片可见，不裁剪）
              if (arImage >= arCanvas) {
                // 图片更宽或相等：以长边（宽）铺满画布宽
                normWidth = 1;
                normHeight = arCanvas / arImage; // 高 < 1
                scale = 1;
                normLeft = 0;
                normTop = (1 - normHeight) / 2; // 居中
              } else {
                // 图片更高：以长边（高）铺满画布高
                normHeight = 1;
                normWidth = arImage / arCanvas; // 宽 < 1
                scale = 1;
                normTop = 0;
                normLeft = (1 - normWidth) / 2; // 居中
              }
            }
            let layer = {
              type: 'Image',
              text: undefined,
              ai: undefined,
              shapeType: undefined,
              src: imageResource?.content,
              // relate position 
              options: {
                left: normLeft + normWidth / 2,
                top: normTop + normHeight / 2,
                width: normWidth,
                height: normHeight,
              },
              isResource: true,
              scaleX: scale,
              scaleY: scale,
              width: iw,
              height: ih,
              lock: false,
              fontSize: undefined,
              fontFamily: undefined,
              fontWeight: undefined,
              rotation: undefined,
              frameSize: undefined,
              fill: "rgb(0,0,0)",
              textAlign: undefined,
              shadow: undefined,
              transform: undefined,
              frameStyle: undefined,
            }
            if ((imageResource as any)?.ai) {
              layer.ai = (imageResource as any)?.ai;
            }
            updatedFace.layers.push(layer);
          }

          const updatedDesign = {
            ...template.design,
            faces: [updatedFace]
          };

          return {
            ...template,
            design: updatedDesign
          };
        }
        // 如果模板没有设计数据，直接返回原模板
        return template;
      });

      return {
        ...product,
        templates: updatedTemplates
      };
    }
    return product;
  });

  return productsWithUpdatedLayers;
};
